import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { usePermissions } from '../hooks/usePermissions';
import { PERMISSIONS } from '../lib/permissions';
import {
  Plus,
  Trash2,
  Edit2,
  Eye,
  EyeOff,
  Image,
  Loader2,
  ArrowRight,
  Heart,
  Sparkles,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import MediaPicker from '../components/MediaPicker';

interface AhJimIllustration {
  id: number;
  title: string;
  imageUrl: string;
  quote: string;
  isActive: number;
  sortOrder: number;
  createdAt: number;
}

interface TemplateComponent {
  type: 'HEADER' | 'BODY' | 'FOOTER' | 'BUTTONS';
  format?: 'TEXT' | 'IMAGE' | 'VIDEO' | 'DOCUMENT';
  text?: string;
  example?: Record<string, unknown>;
  buttons?: Array<Record<string, unknown>>;
}

interface WhatsAppTemplate {
  name: string;
  status: 'APPROVED' | 'PENDING' | 'REJECTED' | string;
  language: string;
  category: string;
  components?: TemplateComponent[];
  quality_score?: string;
}

const ACTIVE_TEMPLATE_NAMES = [
  'payment_confirmation_v3',
  'order_completed_v5',
  'order_received_v1',
  'verification_success_v3',
  'referrer_welcome_v1',
  'commission_notification_v1',
];

type TabKey = 'illustrations' | 'activeTemplates' | 'deletableTemplates';

function SafeImage({ src, alt, className }: { src: string; alt: string; className?: string }) {
  const [error, setError] = useState(false);
  if (error) {
    return (
      <div className={`flex flex-col items-center justify-center bg-gray-100 dark:bg-gray-700 text-gray-400 ${className || ''}`}>
        <Image className="w-6 h-6 mb-1" />
        <span className="text-[10px]">載入失敗</span>
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} onError={() => setError(true)} crossOrigin="anonymous" />;
}

function StatusBadge({ status }: { status: string }) {
  const color =
    status === 'APPROVED'
      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
      : status === 'PENDING'
      ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
      : status === 'REJECTED'
      ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
      : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300';
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {status}
    </span>
  );
}

function ActiveBadge({ active }: { active: boolean }) {
  return active ? (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
      啟用
    </span>
  ) : (
    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300">
      停用
    </span>
  );
}

function getTemplateBodyText(components?: TemplateComponent[]): string {
  if (!components) return '';
  const body = components.find((c) => c.type === 'BODY');
  return body?.text || '';
}

function extractVariables(text: string): string[] {
  const matches = text.match(/\$?\{\{\d+\}\}/g);
  return matches ? [...new Set(matches.map((m) => m.replace(/^\$/, '')))] : [];
}

function EmptyState({ icon: Icon, title, description }: { icon: React.ElementType; title: string; description: string }) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 text-center text-gray-500 dark:text-gray-400">
      <Icon className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
      <p className="text-sm font-medium text-gray-900 dark:text-white">{title}</p>
      <p className="text-sm mt-1">{description}</p>
    </div>
  );
}

export default function AdminAhJimContent() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWrite = can(PERMISSIONS.AHJIM_WRITE);

  const [items, setItems] = useState<AhJimIllustration[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState('');
  const [deletingKey, setDeletingKey] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<TabKey>('illustrations');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ title: '', imageUrl: '', quote: '', sortOrder: 0 });
  const [pickerOpen, setPickerOpen] = useState(false);

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchItems();
    fetchTemplates();
  }, [token, navigate]);

  const { activeTemplates, deletableTemplates } = useMemo(() => {
    const active: WhatsAppTemplate[] = [];
    const deletable: WhatsAppTemplate[] = [];
    for (const t of templates) {
      if (ACTIVE_TEMPLATE_NAMES.includes(t.name)) active.push(t);
      else deletable.push(t);
    }
    return { activeTemplates: active, deletableTemplates: deletable };
  }, [templates]);

  const fetchItems = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiFetch('/api/public/admin/ahjim-illustrations', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setItems(Array.isArray(data) ? data : []);
      } else if (res.status === 401) {
        localStorage.removeItem('admin_token');
        localStorage.removeItem('admin_user');
        navigate('/admin');
      } else {
        setError('載入插圖素材失敗');
      }
    } catch {
      setError('載入插圖素材失敗');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    setTemplatesLoading(true);
    setTemplatesError('');
    try {
      const res = await apiFetch('/api/public/admin/whatsapp/templates', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setTemplatesError(data.error || `載入模板失敗 (${res.status})`);
        return;
      }
      const data = await res.json();
      setTemplates(Array.isArray(data.templates) ? data.templates : []);
    } catch (err) {
      setTemplatesError(err instanceof Error ? err.message : '載入模板失敗');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.imageUrl.trim()) return;
    setError('');
    const method = editingId ? 'PUT' : 'POST';
    const url = editingId
      ? `/api/public/admin/ahjim-illustrations/${editingId}`
      : '/api/public/admin/ahjim-illustrations';
    try {
      const res = await apiFetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setShowForm(false);
        setEditingId(null);
        setForm({ title: '', imageUrl: '', quote: '', sortOrder: 0 });
        fetchItems();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.error || '儲存失敗');
      }
    } catch {
      setError('儲存失敗');
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('確定刪除這張插圖？')) return;
    try {
      const res = await apiFetch(`/api/public/admin/ahjim-illustrations/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) fetchItems();
    } catch {
      setError('刪除失敗');
    }
  };

  const toggleActive = async (item: AhJimIllustration) => {
    try {
      const res = await apiFetch(`/api/public/admin/ahjim-illustrations/${item.id}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: !item.isActive }),
      });
      if (res.ok) fetchItems();
    } catch {
      setError('更新狀態失敗');
    }
  };

  const handleDeleteTemplate = async (template: WhatsAppTemplate) => {
    if (!confirm(`確定刪除模板「${template.name}（${template.language}）」？此操作無法復原。`)) return;
    const key = `${template.name}:${template.language}`;
    setDeletingKey(key);
    try {
      const res = await apiFetch(
        `/api/public/admin/whatsapp/templates/${encodeURIComponent(template.name)}/${encodeURIComponent(template.language)}`,
        {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (res.ok) {
        fetchTemplates();
      } else {
        const data = await res.json().catch(() => ({}));
        setTemplatesError(data.error || `刪除失敗 (${res.status})`);
      }
    } catch (err) {
      setTemplatesError(err instanceof Error ? err.message : '刪除失敗');
    } finally {
      setDeletingKey(null);
    }
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ title: '', imageUrl: '', quote: '', sortOrder: 0 });
    setShowForm(true);
  };

  const openEdit = (item: AhJimIllustration) => {
    setEditingId(item.id);
    setForm({
      title: item.title,
      imageUrl: item.imageUrl,
      quote: item.quote,
      sortOrder: item.sortOrder ?? 0,
    });
    setShowForm(true);
  };

  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  }, [items]);

  const tabs: { key: TabKey; label: string; count?: number }[] = [
    { key: 'illustrations', label: '阿占插圖與語錄', count: items.length },
    { key: 'activeTemplates', label: '使用中 WhatsApp 模板', count: activeTemplates.length },
    { key: 'deletableTemplates', label: '可清理舊版模板', count: deletableTemplates.length },
  ];

  return (
    <AdminLayout currentPage="ahjim-content">
      <div>
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center">
            {error}
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">✕</button>
          </div>
        )}

        {/* Page Header */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                <Heart className="w-6 h-6 text-orange-600 dark:text-orange-400" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">品牌觸點內容庫</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  管理阿占插圖、配套語錄，以及 Meta WhatsApp 模板狀態
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/admin/whatsapp-center')}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-orange-700 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 rounded-lg hover:bg-orange-100 dark:hover:bg-orange-900/30"
            >
              WhatsApp 中心
              <ArrowRight className="w-4 h-4 ml-1" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 border-b dark:border-gray-700 mb-6 rounded-t-lg">
          <nav className="flex space-x-1 px-2" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`py-3 px-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.key
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                {tab.label}
                {typeof tab.count === 'number' && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Illustrations Tab */}
        {activeTab === 'illustrations' && (
          <div>
            {canWrite && (
              <div className="mb-6">
                <button
                  onClick={openCreate}
                  className="flex items-center px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  新增插圖
                </button>
              </div>
            )}

            {loading ? (
              <div className="bg-white dark:bg-gray-800 rounded shadow p-8 text-center text-gray-500 dark:text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                載入中...
              </div>
            ) : sortedItems.length === 0 ? (
              <EmptyState
                icon={Image}
                title="暫無插圖素材"
                description="點擊「新增插圖」開始建立品牌觸點內容"
              />
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">圖片</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">標題</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">語錄</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">排序</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">狀態</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">操作</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {sortedItems.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                          <td className="px-4 py-3">
                            <div className="w-20 aspect-[4/3] rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700">
                              <SafeImage src={item.imageUrl} alt={item.title} className="w-full h-full object-cover" />
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white">
                            {item.title}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400 max-w-xs">
                            <p className="line-clamp-2 italic">「{item.quote}」</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                            {item.sortOrder ?? 0}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <ActiveBadge active={!!item.isActive} />
                          </td>
                          <td className="px-4 py-3 text-right whitespace-nowrap">
                            <div className="flex items-center justify-end gap-2">
                              {canWrite && (
                                <>
                                  <button
                                    onClick={() => toggleActive(item)}
                                    className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600"
                                    title={item.isActive ? '停用' : '啟用'}
                                  >
                                    {item.isActive ? (
                                      <><EyeOff className="w-3 h-3 mr-1" /> 停用</>
                                    ) : (
                                      <><Eye className="w-3 h-3 mr-1" /> 啟用</>
                                    )}
                                  </button>
                                  <button
                                    onClick={() => openEdit(item)}
                                    className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-lg bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 hover:bg-orange-200 dark:hover:bg-orange-900/50"
                                  >
                                    <Edit2 className="w-3 h-3 mr-1" /> 編輯
                                  </button>
                                  <button
                                    onClick={() => handleDelete(item.id)}
                                    className="inline-flex items-center px-2.5 py-1.5 text-xs font-medium rounded-lg bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50"
                                  >
                                    <Trash2 className="w-3 h-3 mr-1" /> 刪除
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Active Templates Tab */}
        {activeTab === 'activeTemplates' && (
          <div>
            {templatesError && (
              <div className="mb-4 px-4 py-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm flex justify-between items-center">
                {templatesError}
                <button onClick={() => setTemplatesError('')} className="text-red-500 hover:text-red-700">✕</button>
              </div>
            )}

            {templatesLoading ? (
              <div className="bg-white dark:bg-gray-800 rounded shadow p-8 text-center text-gray-500 dark:text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                載入模板中...
              </div>
            ) : activeTemplates.length === 0 ? (
              <EmptyState
                icon={Sparkles}
                title="暫無使用中的模板"
                description="請到 WhatsApp 中心建立或同步核心模板"
              />
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {activeTemplates.map((t) => {
                  const bodyText = getTemplateBodyText(t.components);
                  const variables = extractVariables(bodyText);
                  return (
                    <div
                      key={`${t.name}:${t.language}`}
                      className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 p-4 flex flex-col"
                    >
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <h3 className="text-sm font-bold text-gray-900 dark:text-white break-all">{t.name}</h3>
                        <StatusBadge status={t.status} />
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 mb-3 space-x-2">
                        <span>語言：{t.language}</span>
                        <span>類別：{t.category}</span>
                      </div>
                      {bodyText && (
                        <div className="flex-1 bg-gray-50 dark:bg-gray-900 rounded-lg p-3 border border-gray-100 dark:border-gray-700 mb-3">
                          <pre className="text-xs text-gray-700 dark:text-gray-300 whitespace-pre-wrap font-mono leading-relaxed line-clamp-6">
                            {bodyText}
                          </pre>
                        </div>
                      )}
                      {variables.length > 0 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          變數：{variables.join(', ')}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Deletable Templates Tab */}
        {activeTab === 'deletableTemplates' && (
          <div>
            {templatesError && (
              <div className="mb-4 px-4 py-3 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 rounded-lg text-sm flex justify-between items-center">
                {templatesError}
                <button onClick={() => setTemplatesError('')} className="text-red-500 hover:text-red-700">✕</button>
              </div>
            )}

            {templatesLoading ? (
              <div className="bg-white dark:bg-gray-800 rounded shadow p-8 text-center text-gray-500 dark:text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                載入模板中...
              </div>
            ) : deletableTemplates.length === 0 ? (
              <EmptyState
                icon={Sparkles}
                title="沒有可清理的舊版模板"
                description="所有 Meta 模板均為系統使用中，無需清理"
              />
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">名稱</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">狀態</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">語言</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">類別</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">操作</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {deletableTemplates.map((t) => {
                        const key = `${t.name}:${t.language}`;
                        return (
                          <tr key={key} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                            <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white break-all max-w-xs">
                              {t.name}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <StatusBadge status={t.status} />
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                              {t.language}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">
                              {t.category}
                            </td>
                            <td className="px-4 py-3 text-right whitespace-nowrap">
                              <button
                                onClick={() => handleDeleteTemplate(t)}
                                disabled={deletingKey === key}
                                className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-50"
                              >
                                {deletingKey === key ? (
                                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                ) : (
                                  <Trash2 className="w-3 h-3 mr-1" />
                                )}
                                刪除
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-xl">
              <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
                {editingId ? '編輯插圖' : '新增插圖'}
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">標題</label>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                    placeholder="例如：阿占隨意 #16"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">圖片</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={form.imageUrl}
                      onChange={(e) => setForm((p) => ({ ...p, imageUrl: e.target.value }))}
                      placeholder="https://shop.deliciousexpress.com.hk/media/ahjim/..."
                      className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setPickerOpen(true)}
                      className="px-3 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm whitespace-nowrap"
                    >
                      從媒體庫選擇
                    </button>
                  </div>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    可手動輸入圖片 URL，或從媒體庫選擇已上傳的圖片
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">配套語錄</label>
                  <textarea
                    rows={3}
                    value={form.quote}
                    onChange={(e) => setForm((p) => ({ ...p, quote: e.target.value }))}
                    placeholder="這張插圖配套的語錄..."
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  />
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    這句語錄會在 WhatsApp 訊息中隨機顯示給客戶
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">排序</label>
                  <input
                    type="number"
                    value={form.sortOrder}
                    onChange={(e) => setForm((p) => ({ ...p, sortOrder: Number(e.target.value) || 0 }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white"
                  />
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">數字越小越靠前</p>
                </div>
                {form.imageUrl && (
                  <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden aspect-[4/3] bg-gray-100 dark:bg-gray-700">
                    <SafeImage src={form.imageUrl} alt="預覽" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>
              <div className="flex gap-2 mt-6">
                <button
                  onClick={() => setShowForm(false)}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                >
                  取消
                </button>
                {canWrite && (
                  <button
                    onClick={handleSave}
                    className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700"
                  >
                    儲存
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        <MediaPicker
          isOpen={pickerOpen}
          onClose={() => setPickerOpen(false)}
          onSelect={(url) => setForm((p) => ({ ...p, imageUrl: url }))}
          folder="ahjim"
        />
      </div>
    </AdminLayout>
  );
}
